import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { useFeatureConfig } from '../../../context/FeatureConfigContext';
import { useShift } from '../../../context/ShiftContext';
import { useTeam } from '../../../context/TeamContext';
import { useTheme } from '../../../context/ThemeContext';
import { useEquipment } from '../../../context/EquipmentContext';
import { useNotification } from '../../../context/NotificationContext';
import { TEAM_ROLES } from '../../../data/nurseAppModels';
import { setDocumentTitle } from '../../../utils/documentTitle';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../../firebase/firebaseConfig';
import { getMascotByXP, SHIFT_TYPES } from '../../../data/nurseAppModels';
import NurseBannerWrapper from '../Banner/NurseBannerWrapper';
import NurseBottomNavigation from '../Layout/NurseBottomNavigation';
import CampaignBannerCarousel from '../../Campaign/CampaignBannerCarousel';
import { CAMPAIGN_POSITIONS } from '../../../data/campaignModels';
import { collection, query, where, getDocs, limit, orderBy } from 'firebase/firestore';
import InspectionReminderWidget from '../Equipment/InspectionReminderWidget';

// Default Banner Component for Nurse App
const NurseDefaultBanner = ({ currentTheme }) => {
  return (
    <div 
      className="text-white p-4"
      style={{
        background: `linear-gradient(to right, ${currentTheme.colors.primary}, ${currentTheme.colors.accent})`
      }}
    >
      <div className="container mx-auto">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <p className="text-xs text-white/80 mb-1">ข่าวสุขภาพ</p>
            <h3 className="text-lg font-bold mb-1">นวัตกรรมเพื่อสุขภาพพยาบาล</h3>
            <p className="text-sm text-white/90">เทคโนโลยีและการดูแลตนเองสำหรับคนทำงาน</p>
          </div>
          <button className="ml-4 w-10 h-10 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30 transition">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

const NurseHomePage = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { isFeatureEnabled } = useFeatureConfig();
  const { shifts, getUpcomingShifts, getTodayShifts } = useShift();
  const { currentTeam } = useTeam();
  const { currentTheme } = useTheme();
  const { getPendingInspections } = useEquipment();
  const { unreadCount } = useNotification();
  const [userData, setUserData] = useState(null);
  const [mascot, setMascot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [upcomingShift, setUpcomingShift] = useState(null);
  const [equipmentToCheck, setEquipmentToCheck] = useState(0);
  const [newProducts, setNewProducts] = useState(0);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [teamMembersWithProfile, setTeamMembersWithProfile] = useState([]);

  useEffect(() => {
    // ใช้ title เดียวกันจาก appConfig.js เพื่อ SEO
    setDocumentTitle('', true);
  }, []);

  useEffect(() => {
    if (currentUser) {
      fetchUserData();
      fetchUpcomingShift();
      fetchEquipmentToCheck();
      fetchNewProducts();
    } else {
      setLoading(false);
    }
  }, [currentUser, currentTeam]);

  useEffect(() => {
    // Update upcoming shift from ShiftContext
    const upcoming = getUpcomingShifts();
    if (upcoming && upcoming.length > 0) {
      setUpcomingShift(upcoming[0]);
    }
  }, [getUpcomingShifts]);

  // Fetch team members with profile data
  useEffect(() => {
    const fetchTeamMembersWithProfile = async () => {
      if (!currentTeam || !currentTeam.members) return;

      try {
        const membersWithProfile = await Promise.all(
          currentTeam.members.map(async (member) => {
            try {
              const userRef = doc(db, 'users', member.uid);
              const userSnap = await getDoc(userRef);
              const userData = userSnap.exists() ? userSnap.data() : null;
              
              return {
                ...member,
                photoURL: userData?.photoURL || null,
                displayName: userData?.displayName || member.name || 'Unknown'
              };
            } catch (err) {
              console.error(`Error fetching user ${member.uid}:`, err);
              return {
                ...member,
                photoURL: null,
                displayName: member.name || 'Unknown'
              };
            }
          })
        );
        setTeamMembersWithProfile(membersWithProfile);
      } catch (err) {
        console.error('Error fetching team members:', err);
      }
    };

    if (currentTeam) {
      fetchTeamMembersWithProfile();
    }
  }, [currentTeam]);

  const fetchUserData = async () => {
    try {
      const userRef = doc(db, 'users', currentUser.uid);
      const userSnap = await getDoc(userRef);
      
      if (userSnap.exists()) {
        const data = userSnap.data();
        setUserData(data);
        
        // Calculate mascot
        const xp = data.currentXP || 0;
        const mascotData = getMascotByXP(xp);
        setMascot(mascotData);
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
    } finally {
      setLoading(false);
    }
  };


  const fetchUpcomingShift = async () => {
    try {
      if (!currentUser) return;
      
      const shiftsRef = collection(db, 'shifts');
      const now = new Date();
      const q = query(
        shiftsRef,
        where('assignedTo', '==', currentUser.uid),
        where('status', 'in', ['SCHEDULED', 'CONFIRMED']),
        orderBy('startTime', 'asc'),
        limit(1)
      );
      
      const snapshot = await getDocs(q);
      if (!snapshot.empty) {
        setUpcomingShift({
          id: snapshot.docs[0].id,
          ...snapshot.docs[0].data()
        });
      }
    } catch (error) {
      console.error('Error fetching upcoming shift:', error);
    }
  };

  const fetchNewProducts = async () => {
    try {
      // Fetch new products even if feature is disabled (for badge display)
      const productsRef = collection(db, 'products');
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const q = query(
        productsRef,
        where('createdAt', '>=', today),
        where('status', '==', 'active')
      );
      
      const snapshot = await getDocs(q);
      setNewProducts(snapshot.size);
    } catch (error) {
      console.error('Error fetching new products:', error);
      setNewProducts(0);
    }
  };

  const fetchEquipmentToCheck = async () => {
    try {
      if (!currentUser || !currentTeam) return;
      
      // Get pending inspections for today
      const pendingInspections = await getPendingInspections(new Date());
      setEquipmentToCheck(pendingInspections.length);
    } catch (error) {
      console.error('Error fetching equipment to check:', error);
      setEquipmentToCheck(0);
    }
  };

  // Get month name in Thai
  const getMonthName = (date) => {
    const months = [
      'มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน',
      'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม'
    ];
    return months[date.getMonth()];
  };

  // Get day name in Thai
  const getDayName = (date) => {
    const days = ['อา', 'จ', 'อ', 'พ', 'พฤ', 'ศ', 'ส'];
    return days[date.getDay()];
  };

  // Generate 5 days starting from selected date
  const getDateRange = () => {
    const dates = [];
    const startDate = new Date(selectedDate);
    startDate.setDate(startDate.getDate() - 2); // Start 2 days before
    
    for (let i = 0; i < 5; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      dates.push(date);
    }
    return dates;
  };

  const dateRange = getDateRange();

  // Get shifts for selected date
  const getShiftsForDate = (date) => {
    if (!shifts || shifts.length === 0) return [];
    const dateStr = date.toISOString().split('T')[0];
    return shifts.filter(shift => {
      const shiftDate = shift.startTime?.toDate ? shift.startTime.toDate() : 
                       shift.startTime?.seconds ? new Date(shift.startTime.seconds * 1000) : null;
      if (!shiftDate) return false;
      const shiftDateStr = shiftDate.toISOString().split('T')[0];
      return shiftDateStr === dateStr;
    });
  };

  // Get shift type icon
  const getShiftTypeIcon = (type) => {
    switch (type) {
      case SHIFT_TYPES.MORNING: return '🌅';
      case SHIFT_TYPES.AFTERNOON: return '☀️';
      case SHIFT_TYPES.NIGHT: return '🌙';
      default: return '📅';
    }
  };

  // Get user profile image
  const getUserProfileImage = (userId) => {
    const member = teamMembersWithProfile.find(m => m.uid === userId);
    return member?.photoURL || null;
  };

  // Get user display name
  const getUserDisplayName = (userId) => {
    const member = teamMembersWithProfile.find(m => m.uid === userId);
    return member?.displayName || 'Unknown';
  };

  // Check if date has shifts
  const dateHasShifts = (date) => {
    return getShiftsForDate(date).length > 0;
  };

  // Check if date is today
  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  // Check if date is selected
  const isSelected = (date) => {
    return date.toDateString() === selectedDate.toDateString();
  };


  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">กำลังโหลด...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Health News Banner - Using Campaign Banner System */}
      <div className="mb-4">
        <NurseBannerWrapper
          positionId={CAMPAIGN_POSITIONS.NURSE_HOMEPAGE_TOP || 'nurse-homepage-top'}
          fallback={null}
        />
      </div>

      {/* Marketplace Banner - Same as Marketplace Home */}
      <div className="mb-4">
        <CampaignBannerCarousel
          positionId={CAMPAIGN_POSITIONS.HOMEPAGE_TOP}
          userStoreId={currentUser?.uid}
          fallback={
            <div 
              className="text-white p-4"
              style={{
                background: `linear-gradient(to right, ${currentTheme.colors.primary}, ${currentTheme.colors.accent})`
              }}
            >
              <div className="container mx-auto">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-xs text-white/80 mb-1">ตลาดซื้อ-ขาย</p>
                    <h3 className="text-lg font-bold mb-1">อุปกรณ์และเครื่องมือสำหรับพยาบาล</h3>
                    <p className="text-sm text-white/90">ซื้อ-ขายอุปกรณ์ทางการแพทย์ เครื่องแบบ และสิ่งของที่จำเป็น</p>
                  </div>
                  <div 
                    className="ml-4 w-10 h-10 bg-white/20 rounded-full flex items-center justify-center opacity-50 cursor-not-allowed"
                    title="ยังไม่เปิดให้บริการ"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          }
        />
      </div>

      {/* Daily Date Selection */}
      {currentTeam ? (
        <div className="container mx-auto px-4 py-4">
          <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <span className="text-lg font-semibold text-gray-900">
                  {getMonthName(selectedDate)}
                </span>
                <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
              <button
                onClick={() => navigate('/nurse/shifts')}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                ดูทั้งหมด →
              </button>
            </div>
            <div className="flex gap-2 overflow-x-auto pb-2">
              {dateRange.map((date, index) => {
                const hasShifts = dateHasShifts(date);
                const isSelectedDate = isSelected(date);
                const isTodayDate = isToday(date);

                return (
                  <button
                    key={index}
                    onClick={() => {
                      setSelectedDate(date);
                      navigate('/nurse/shifts');
                    }}
                    className={`flex-shrink-0 px-4 py-3 rounded-lg transition ${
                      isSelectedDate
                        ? 'bg-orange-500 text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
                    }`}
                  >
                    <div className="text-center">
                      <div className="text-sm font-medium">
                        {date.getDate()}
                      </div>
                      <div className="text-xs mt-1">
                        {getDayName(date)}
                      </div>
                      {hasShifts && (
                        <div className="mt-1 flex items-center justify-center gap-0.5 flex-wrap">
                          {getShiftsForDate(date).slice(0, 3).map((shift, shiftIndex) => {
                            const profileImage = getUserProfileImage(shift.assignedTo);
                            return (
                              <div key={shiftIndex} className="relative">
                                {profileImage ? (
                                  <img
                                    src={profileImage}
                                    alt={getUserDisplayName(shift.assignedTo)}
                                    className={`w-4 h-4 rounded-full border ${
                                      isSelectedDate ? 'border-white' : 'border-orange-500'
                                    }`}
                                    title={`${getShiftTypeIcon(shift.shiftType)} ${getUserDisplayName(shift.assignedTo)}`}
                                  />
                                ) : (
                                  <div
                                    className={`w-4 h-4 rounded-full text-[8px] flex items-center justify-center border ${
                                      isSelectedDate
                                        ? 'bg-white text-orange-500 border-white'
                                        : 'bg-orange-500 text-white border-orange-500'
                                    }`}
                                    title={`${getShiftTypeIcon(shift.shiftType)} ${getUserDisplayName(shift.assignedTo)}`}
                                  >
                                    {getShiftTypeIcon(shift.shiftType)}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                          {getShiftsForDate(date).length > 3 && (
                            <span className={`text-[8px] ${
                              isSelectedDate ? 'text-white' : 'text-orange-500'
                            }`}>
                              +{getShiftsForDate(date).length - 3}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
            {/* Team Badge - Bottom */}
            <div className="mt-3 pt-3 border-t border-gray-100">
              <p className="text-xs text-gray-400 text-center truncate">
                👥 ทีม: {currentTeam.name}
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="container mx-auto px-4 py-4">
          <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
            <div className="text-center py-4">
              <p className="text-gray-600 mb-2">กรุณาเลือกทีมเพื่อดูแผนเวร</p>
              <button
                onClick={() => navigate('/nurse/teams')}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                ไปที่หน้าทีม →
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Overview Cards */}
      <div className="container mx-auto px-4 py-4">
        <h2 className="text-xl font-bold text-gray-800 mb-4">ภาพรวมของคุณ</h2>
        
        <div className="grid grid-cols-2 gap-4 mb-6">
          {/* Shift Management */}
          <Link
            to="/nurse/shifts"
            className="bg-white rounded-lg shadow-md p-4 hover:shadow-lg transition relative flex flex-col"
          >
            <div className="flex items-center justify-center mb-2">
              <div className="text-4xl">📅</div>
            </div>
            <h3 className="font-semibold text-gray-800 mb-1 text-center">จัดการเวร</h3>
            {upcomingShift && upcomingShift.startTime?.toDate ? (
              <p className="text-sm text-gray-600 text-center">
                เวรถัดไป: {new Date(upcomingShift.startTime.toDate()).toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' })}
              </p>
            ) : (
              <p className="text-sm text-gray-600 text-center">ไม่มีเวรที่กำลังจะมาถึง</p>
            )}
            {currentTeam && (
              <div className="mt-auto pt-2">
                <p className="text-xs text-gray-400 text-center truncate">
                  👥 {currentTeam.name}
                </p>
              </div>
            )}
          </Link>

          {/* Equipment Management - Using Widget */}
          <div className="bg-white rounded-lg shadow-md p-4 hover:shadow-lg transition">
            <InspectionReminderWidget compact={true} />
          </div>

          {/* Marketplace - Coming Soon */}
          <div className="bg-white rounded-lg shadow-md p-4 opacity-60 cursor-not-allowed relative">
            <div className="flex items-center justify-center mb-2">
              <div className="text-4xl opacity-50">🛒</div>
            </div>
            <h3 className="font-semibold text-gray-800 mb-1 text-center">Marketplace (ยังไม่เปิดให้บริการ)</h3>
            {newProducts > 0 ? (
              <>
                <p className="text-sm text-gray-600 text-center mb-2">ตลาดซื้อ-ขายสินค้า</p>
                <div className="absolute top-2 right-2 bg-red-500 text-white text-xs font-bold rounded-full min-w-[24px] h-6 flex items-center justify-center shadow-lg" style={{ zIndex: 10, opacity: 1 }}>
                  {newProducts > 99 ? '99+' : newProducts}
                </div>
              </>
            ) : (
              <p className="text-sm text-gray-600 text-center mb-2">ตลาดซื้อ-ขายสินค้า</p>
            )}
          </div>

          {/* Gamification - Coming Soon */}
          <div className="bg-white rounded-lg shadow-md p-4 opacity-60 cursor-not-allowed">
            <div className="flex items-center justify-center mb-2">
              <div className="text-4xl opacity-50">🏆</div>
            </div>
            <h3 className="font-semibold text-gray-800 mb-1 text-center">Gamification (ยังไม่เปิดให้บริการ)</h3>
            <p className="text-sm text-gray-600 text-center mb-2">เกมส์สะสมประสบการณ์ (ยังไม่เปิดให้บริการ)</p>
          </div>
        </div>

        {/* Today's Summary */}
        {currentTeam ? (
          <div className="bg-white rounded-lg shadow-md p-4 mb-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-gray-800">สรุปข้อมูลวันนี้</h3>
              <p className="text-xs text-gray-400">👥 {currentTeam.name}</p>
            </div>
            
            {/* Upcoming Shift */}
            {upcomingShift && upcomingShift.startTime?.toDate ? (
              <div className="bg-gray-50 rounded-lg p-4 mb-3">
                <p className="text-sm text-gray-600 mb-2">📅 เวรที่กำลังจะมาถึง</p>
                <p className="text-lg font-bold text-gray-800 mb-1">
                  เวร{upcomingShift.shiftType === 'MORNING' ? 'เช้า' : 
                      upcomingShift.shiftType === 'AFTERNOON' ? 'บ่าย' : 'ดึก'}: {' '}
                  {new Date(upcomingShift.startTime.toDate()).toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' })} - {' '}
                  {new Date(upcomingShift.endTime?.toDate()).toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' })}
                </p>
                <p className="text-sm text-gray-600 mb-3">{upcomingShift.department || 'แผนกอายุรกรรม'}</p>
                <Link
                  to={`/nurse/shifts/${upcomingShift.id}`}
                  className="inline-block px-4 py-2 text-white rounded-lg transition text-sm"
                  style={{
                    backgroundColor: currentTheme.colors.primary
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.opacity = '0.9';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.opacity = '1';
                  }}
                >
                  ดูรายละเอียด
                </Link>
              </div>
            ) : (
              <div className="bg-gray-50 rounded-lg p-4 mb-3 text-center">
                <p className="text-gray-600 text-sm">ไม่มีเวรที่กำลังจะมาถึง</p>
              </div>
            )}

            {/* Equipment to Inspect */}
            {equipmentToCheck > 0 ? (
              <div className="bg-red-50 rounded-lg p-4 mb-3">
                <p className="text-sm text-gray-600 mb-2">🩺 อุปกรณ์ที่ต้องตรวจสอบ</p>
                <p className="text-lg font-bold text-red-700 mb-3">{equipmentToCheck} รายการ</p>
                <Link
                  to="/nurse/equipment"
                  className="inline-block px-4 py-2 bg-red-600 text-white rounded-lg transition text-sm hover:bg-red-700"
                >
                  ไปที่หน้าอุปกรณ์
                </Link>
              </div>
            ) : (
              <div className="bg-gray-50 rounded-lg p-4 mb-3 text-center">
                <p className="text-gray-600 text-sm">✅ ไม่มีอุปกรณ์ที่ต้องตรวจสอบ</p>
              </div>
            )}

            {/* Notifications */}
            {unreadCount > 0 ? (
              <div className="bg-yellow-50 rounded-lg p-4">
                <p className="text-sm text-gray-600 mb-2">🔔 การแจ้งเตือน</p>
                <p className="text-lg font-bold text-yellow-700 mb-3">{unreadCount} รายการ</p>
                <Link
                  to="/nurse/notifications"
                  className="inline-block px-4 py-2 bg-yellow-600 text-white rounded-lg transition text-sm hover:bg-yellow-700"
                >
                  ดูการแจ้งเตือน
                </Link>
              </div>
            ) : (
              <div className="bg-gray-50 rounded-lg p-4 text-center">
                <p className="text-gray-600 text-sm">✅ ไม่มีการแจ้งเตือนใหม่</p>
              </div>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-md p-4 mb-6">
            <h3 className="font-semibold text-gray-800 mb-3">สรุปข้อมูลวันนี้</h3>
            <div className="bg-gray-50 rounded-lg p-4 text-center">
              <p className="text-gray-600 mb-2">กรุณาเลือกทีมเพื่อดูสรุปข้อมูล</p>
              <Link
                to="/nurse/teams"
                className="text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                ไปที่หน้าทีม →
              </Link>
            </div>
          </div>
        )}

        {/* Progress Section */}
        {mascot && (
          <div className="bg-white rounded-lg shadow-md p-4 mb-6">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold text-gray-800">
                ความคืบหน้า: Level {mascot.level}
              </h3>
              <span className="text-sm text-gray-600">
                XP {mascot.xp} {mascot.xpToNext && `/ ${mascot.xpToNext}`}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2 mb-3">
              <div
                className="h-2 rounded-full transition-all duration-300"
                style={{ 
                  width: `${mascot.progressPercent}%`,
                  backgroundColor: currentTheme.colors.primary
                }}
              ></div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">เหรียญล่าสุด:</span>
              <div className="flex gap-2">
                <div className="w-8 h-8 rounded-full bg-yellow-100 flex items-center justify-center">
                  <span className="text-lg">🛡️</span>
                </div>
                <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                  <span className="text-lg">⭐</span>
                </div>
                <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                  <span className="text-lg">⏰</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Bottom Navigation */}
      <NurseBottomNavigation />
    </div>
  );
};

export default NurseHomePage;

